import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import {
  fetchJobApplicationData,
  updateApplicationData,
  updateApplicationDataForStageTwo,
  updateCandidateAnswerByAdmin,
} from "../../../redux/jobsSlice.ts";
import { Loader } from "../../../components/loader/index.js";
import send from "../../../assets/svg/send.svg";
import cross from "../../../assets/svg/cross.svg";
import { convertDateToWords } from "../../../helper/dateFormat.js";
import { Breadcrumbs } from "../../../components/breadcrumbs/index.jsx";
import { FaCheck, FaTimes } from "react-icons/fa";
import { toastNotification } from "../../../services/notification-service.js";

export const JobApplication = () => {
  const { jobApplicationData, isLoading } = useSelector((store) => store.jobs);
  const [data, setData] = useState({ status: 0, test_taken: 1 });
  const dispatch = useDispatch();
  const { id } = useParams();

  // Personal Information Section
  const personalDetails = [
    { key: "Name", value: jobApplicationData?.name },
    { key: "Email", value: jobApplicationData?.email },
    { key: "Phone", value: jobApplicationData?.phone },
    { key: "Address", value: jobApplicationData?.address },
  ];

  // Professional Information Section
  const professionalDetails = [
    { key: "LinkedIn", value: jobApplicationData?.linkedin },
    { key: "Short Pitch", value: jobApplicationData?.short_pitch },
    {
      key: "Current Salary",
      value: jobApplicationData?.current_salary || "Not specified",
    },
    {
      key: "Expected Salary",
      value: jobApplicationData?.expected_salary || "Not specified",
    },
    {
      key: "Notice Period",
      value: `${jobApplicationData?.notice_period || 0} days`,
    },
    {
      key: "Resume",
      value: jobApplicationData?.resume_path ? (
        <a
          className="inline-flex items-center hover:opacity-70 text-white bg-orange-500 border-orange-500 border-2 text-sm px-3 py-1 outline-none rounded-full cursor-pointer"
          href={jobApplicationData?.resume_path}
          target="_blank"
          rel="noreferrer"
          download={`${jobApplicationData?.name}_Resume.pdf`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-4 h-4 mr-1"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9.75v6.75m0 0-3-3m3 3 3-3m-8.25 6a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z"
            />
          </svg>
          Download
        </a>
      ) : (
        "Not available"
      ),
    },
  ];

  // Application Information Section
  const applicationDetails = [
    {
      key: "Applied On",
      value: convertDateToWords(jobApplicationData?.created_at?.slice(0, 10)),
    },
    { key: "Status", value: getStatusText(jobApplicationData?.status) },
    {
      key: jobApplicationData?.status === 1 ? "Accepted On" : "Rejected On",
      value:
        jobApplicationData?.status === 1
          ? convertDateToWords(jobApplicationData?.updated_at)
          : jobApplicationData?.rejected_on
          ? convertDateToWords(jobApplicationData.rejected_on)
          : "N/A",
    },
    {
      key: "Admin Comment",
      value: jobApplicationData?.admin_message || "No comments",
    },
  ];

  // Helper function to get status text
  function getStatusText(status) {
    switch (status) {
      case 0:
        return <span className="text-orange-600">Pending</span>;
      case 1:
        return <span className="text-green-600">Accepted</span>;
      case 2:
        return <span className="text-red-600">Rejected</span>;
      default:
        return "Unknown";
    }
  }

  const AnswerRenderer = ({ question, answer, isAdmin = false ,job_id}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedIsCorrect, setEditedIsCorrect] = useState(
      answer?.is_correct || false
    );
    const [editedNotes, setEditedNotes] = useState(answer?.admin_notes || "");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSave = async () => {
      try {
        setIsSubmitting(true);

        const response = await dispatch(
          updateCandidateAnswerByAdmin({
            job_id:job_id,
            answer_id: answer.id,
            is_correct: editedIsCorrect,
            admin_notes: editedNotes,
          })
        );
        setIsEditing(false);
      } catch (error) {
        console.error("Update failed:", error);
        toastNotification({
          status: "error",
          message: error.message || "Failed to update answer",
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    if (!answer) return <span>No answer provided</span>;

    if (isEditing) {
      return (
        <div className="space-y-2">
          <div className="flex items-center">
            <label className="flex items-center mr-4">
              <input
                type="checkbox"
                checked={editedIsCorrect}
                onChange={(e) => setEditedIsCorrect(e.target.checked)}
                className="mr-2"
              />
              Mark as Correct
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Admin Notes
              <span className="text-xs text-gray-500 ml-1">
                ({editedNotes.length}/255 characters)
              </span>
            </label>
            <textarea
              value={editedNotes}
              onChange={(e) => {
                if (e.target.value.length <= 255) {
                  setEditedNotes(e.target.value);
                }
              }}
              className="w-full p-2 border rounded"
              rows={3}
              maxLength={255}
            />
            {editedNotes.length >= 250 && (
              <p className="text-xs mt-1 text-red-500">
                {255 - editedNotes.length} characters remaining
              </p>
            )}
          </div>

          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              disabled={isSubmitting}
              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    // View mode
    return (
      <div className="space-y-2">
        <div className="flex items-start">
          <span className="text-orange-500 shrink-0 mr-2">Answer:</span>
          <div>
            <div className="flex items-start">
              {answer.stage === 2 && answer.answer && (
                <a
                  href={answer.answer}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline mr-2"
                >
                  View File
                </a>
              )}

              <span className="mr-2 mt-1">
                {answer.is_correct ? (
                  <FaCheck className="text-green-500" />
                ) : (
                  <FaTimes className="text-red-500" />
                )}
              </span>

              {answer.stage !== 2 && (
                <span className="mr-2">
                  {question.answer_type === "boolean" ? (
                    answer.answer === "true" ? (
                      "Yes"
                    ) : (
                      "No"
                    )
                  ) : question.answer_type === "varchar" ? (
                    <div
                      className="max-h-[7.5rem] overflow-y-auto"
                      style={{
                        lineHeight: "1.5rem",
                        scrollbarWidth: "thin",
                        scrollbarColor: "#cbd5e0 #f1f5f9",
                      }}
                    >
                      {answer.answer}
                    </div>
                  ) : (
                    answer.answer
                  )}
                </span>
              )}
            </div>

            {!answer.is_correct && question.correct_answer && (
              <span className="ml-2 text-sm text-gray-500">
                (Correct answer:{" "}
                {question.answer_type === "boolean"
                  ? question.correct_answer === "true"
                    ? "Yes"
                    : "No"
                  : question.correct_answer}
                )
              </span>
            )}
          </div>
        </div>
        {question.is_justified && answer.justification && (
          <div className="flex items-start">
            <span className="text-orange-500 shrink-0 mr-2">
              Justification:
            </span>
            <span className="mr-2">{answer.justification}</span>
          </div>
        )}

        {isAdmin && (
          <button
            onClick={() => setIsEditing(true)}
            className="mt-2 px-2 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Edit Evaluation
          </button>
        )}
      </div>
    );
  };

  const buttonData = [
    {
      label: "Approve",
      action: () => {
        const hasStageTwoQuestions = jobApplicationData.job_questions?.some(
          (o) => o.stage === 2
        );
        if (!jobApplicationData.notify_date && hasStageTwoQuestions) {
          statusUpdateStageTwo();
        } else {
          setData({ ...data, status: 1 });
        }
      },
      className:
        "shrink-0 border-green-600 text-green-600 hover:bg-green-600 bg-transparent px-3 py-1 border-2 rounded-full hover:text-white transition-all duration-200 ease-in-out",
    },
    {
      label: "Reject",
      action: () => setData({ ...data, status: 2 }),
      className:
        "shrink-0 border-red-600 text-red-600 hover:bg-red-600 bg-transparent px-3 py-1 border-2 rounded-full hover:text-white transition-all duration-200 ease-in-out",
    },
    // Only include this button if notify_date exists
    ...(jobApplicationData.notify_date
      ? [
          {
            label: "Send Again Round 2 Invite",
            action: () => statusUpdateStageTwo(),
            className:
              "shrink-0 border-yellow-500 text-yellow-500 hover:bg-yellow-500 bg-transparent px-3 py-1 border-2 rounded-full hover:text-white transition-all duration-200 ease-in-out",
          },
        ]
      : []),
  ];

  useEffect(() => {
    dispatch(fetchJobApplicationData(id));
  }, [dispatch, id]);

  const statusUpdate = () => {
    dispatch(updateApplicationData({ ...data, id: jobApplicationData?.id }));
  };

  const statusUpdateStageTwo = () => {
    const now = new Date();

    const notifyDate = jobApplicationData?.notify_date
      ? new Date(jobApplicationData.notify_date)
      : null;

    const canDispatch =
      !notifyDate || (notifyDate && now - notifyDate >= 60 * 60 * 1000);

    if (!canDispatch) {
      toastNotification({
        status: "error",
        message:
          "Second stage assessment can only be accessed 1 hour after notification",
      });
      return;
    }

    dispatch(
      updateApplicationDataForStageTwo({
        ...data,
        id: jobApplicationData?.id,
        job_id: jobApplicationData?.job_id,
        user_id: jobApplicationData.user_id,
      })
    );
  };

  return (
    <>
      <Breadcrumbs
        breadcrumbs={[
          { name: "Jobs", link: "/jobs/view-all" },
          {
            name: `Applicants (${jobApplicationData?.job?.title || "Job"})`,
            link: `/jobs/view-applicants/${jobApplicationData?.job_id}`,
          },
          {
            name: `Job Application (${
              jobApplicationData?.name || "Applicant"
            })`,
            link: "",
          },
        ]}
      />

      {isLoading?.jobApplicationData ? (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <Loader h="20" w="20" color={"orange"} />
        </div>
      ) : (
        <div className="mb-1 relative lg:px-12 px-3">
          <div className="shadow-xl p-4 pb-7 rounded-lg">
            {/* Personal Information Section */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-orange-600 border-b pb-2">
                Personal Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {personalDetails.map((detail, index) => (
                  <div key={index} className="flex flex-col">
                    <span className="text-orange-500 font-medium">
                      {detail.key}
                    </span>
                    <span className="text-gray-800">
                      {detail.key === "Name" ? (
                        <Link
                          target="_blank"
                          to={`/users/user-detail/${jobApplicationData?.user_id}`}
                          className="hover:underline"
                        >
                          {detail.value}
                        </Link>
                      ) : (
                        detail.value || "N/A"
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Professional Information Section */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-orange-600 border-b pb-2">
                Professional Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {professionalDetails.map((detail, index) => (
                  <div key={index} className="flex flex-col">
                    <span className="text-orange-500 font-medium">
                      {detail.key}
                    </span>
                    <span className="text-gray-800">
                      {detail.key === "LinkedIn" && detail.value ? (
                        <a
                          href={detail.value}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          View Profile
                        </a>
                      ) : (
                        detail.value || "N/A"
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Application Information Section */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-orange-600 border-b pb-2">
                Application Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {applicationDetails.map((detail, index) => (
                  <div key={index} className="flex flex-col">
                    <span className="text-orange-500 font-medium">
                      {detail.key}
                    </span>
                    <span className="text-gray-800">{detail.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Application Answers */}
            {jobApplicationData?.job_questions?.length > 0 && (
              <>
                {/* Stage 1 - Keep existing implementation */}
                {[1, 2].map((stage) => {
                  if (stage === 2 && !jobApplicationData.notify_date)
                    return null;

                  const filteredQuestions =
                    jobApplicationData.job_questions.filter(
                      (q) => q.stage === stage
                    );

                  if (filteredQuestions.length === 0) return null;

                  // Special handling for Stage 2
                  if (stage === 2) {
                    const answer = jobApplicationData?.candidate_answers?.find(
                      (a) => a.stage === 2
                    );

                    return (
                      <div key={stage} className="mb-8">
                        <h2 className="text-xl font-semibold mb-4 text-orange-600 border-b pb-2">
                          Second Round of Assessment
                        </h2>
                        <div className="flex flex-wrap -mx-2">
                          {/* Left side - All Stage 2 questions */}
                          <div className="w-full md:w-1/2 px-2 mb-4">
                            <div className="bg-gray-50 p-4 rounded-lg h-full">
                              {filteredQuestions.map((quest, index) => (
                                <div key={quest.id} className="mb-4 last:mb-0">
                                  <div className="font-medium text-gray-800 mb-2">
                                    {index + 1}. {quest.question}
                                  </div>
                                  <div className="ml-4 space-y-2">
                                    <div>
                                      <span className="text-orange-500">
                                        Time Limit:{" "}
                                      </span>
                                      <span className="text-gray-800">
                                        {quest.time_limit_seconds} seconds
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="w-full md:w-1/2 px-2 mb-4">
                            <div className="bg-gray-50 p-4 rounded-lg h-full">
                              <div className="font-medium text-gray-800 mb-2">
                                Candidate Submission
                              </div>
                              <div className="ml-4 space-y-2">
                                {answer ? (
                                  <>
                                    <div className="flex items-center mb-1">
                                      <a
                                        href={answer.answer}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 underline mr-2"
                                      >
                                        View File
                                      </a>
                                      <span className="mr-2">
                                        {answer.is_correct ? (
                                          <FaCheck className="text-green-500" />
                                        ) : (
                                          <FaTimes className="text-red-500" />
                                        )}
                                      </span>
                                    </div>
                                    {/* <div>
                                      <span className="text-orange-500">
                                        Time Taken:{" "}
                                      </span>
                                      <span className="text-gray-800">
                                        {answer.time_taken_seconds || 0} seconds
                                      </span>
                                    </div> */}
                                    {answer.admin_notes && (
                                      <div>
                                        <span className="text-orange-500">
                                          Admin Notes:{" "}
                                        </span>
                                        <span className="text-gray-800">
                                          {answer.admin_notes}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span>No Submission provided</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Original Stage 1 implementation
                  return (
                    <div key={stage} className="mb-8">
                      <h2 className="text-xl font-semibold mb-4 text-orange-600 border-b pb-2">
                        Preliminary Assessment Test
                      </h2>
                      <div className="flex flex-wrap -mx-2 word-break">
                        {filteredQuestions.map((quest, index) => {
                          const answer =
                            jobApplicationData?.candidate_answers?.find(
                              (a) => a.question_id === quest.id
                            );

                          return (
                            <div
                              key={quest.id}
                              className="w-full md:w-1/2 px-2 mb-4"
                            >
                              <div className="bg-gray-50 p-4 rounded-lg h-full">
                                {/* Display passage content if available (only once per passage) */}
                                {quest.passage_id && quest.passage_question && (
                                  <div className="mb-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                    <h3 className="font-bold text-lg mb-2 text-orange-600">
                                      {quest.passage_question.title}
                                    </h3>
                                    <div
                                      className="prose max-w-none text-gray-700 overflow-y-auto"
                                      style={{
                                        maxHeight: "4.5em", // approx 3 lines at 1.5em line height
                                        lineHeight: "1.5em",
                                      }}
                                    >
                                      {quest.passage_question?.content &&
                                        `${quest.passage_question.content.replace(
                                          /<[^>]+>/g,
                                          ""
                                        )}`}
                                    </div>
                                  </div>
                                )}
                                {/* Question with image if available */}
                                <div className="font-medium text-gray-800 mb-2">
                                  {index + 1}. {quest.question}
                                  {quest.has_image && quest.image?.path && (
                                    <div className="mt-3 max-w-[150px] mx-auto overflow-hidden rounded bg-gray-100">
                                      <img
                                        src={`${process.env.REACT_APP_SERVER_URL}/${quest?.image?.path}`}
                                        alt="Question illustration"
                                        className="w-full h-auto object-contain"
                                      />
                                    </div>
                                  )}
                                </div>

                                {/* Answer section */}
                                <div className="ml-4 space-y-2">
                                  <div>
                                    <span className="text-orange-500">
                                      Time Taken:{" "}
                                    </span>
                                    <span className="text-gray-800">
                                      {answer?.time_taken_seconds || 0} seconds
                                    </span>
                                  </div>
                                  {answer?.admin_notes && (
                                    <div>
                                      <span className="text-orange-500">
                                        Admin Notes:{" "}
                                      </span>
                                      <span className="text-gray-800">
                                        {answer.admin_notes}
                                      </span>
                                    </div>
                                  )}
                                  <div>
                                    <AnswerRenderer
                                      job_id={jobApplicationData.job_id}
                                      question={quest}
                                      answer={answer}
                                      isAdmin={true}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Status Update Section */}
            {jobApplicationData?.status === 0 && data?.status === 0 && (
              <div className="flex flex-wrap gap-4 mt-8">
                {buttonData
                  .filter((button) => button.label)
                  .map((button, index) => (
                    <button
                      key={index}
                      onClick={button.action}
                      className={button.className}
                    >
                      {button.label}
                    </button>
                  ))}
              </div>
            )}

            {!!data?.status && jobApplicationData?.status === 0 && (
              <div className="flex justify-start items-start flex-col my-6 gap-y-2">
                <span
                  className={`${
                    data?.status === 1 ? "text-green-600" : "text-red-500"
                  }`}
                >
                  {data?.status === 1
                    ? "Approval Message:"
                    : "Reason to Reject"}
                </span>
                <div className="flex flex-col justify-center items-start w-full gap-y-8">
                  <textarea
                    onChange={(e) =>
                      setData({ ...data, message: e.target.value })
                    }
                    maxLength={1000}
                    className={`p-2.5 min-h-[130px] max-h-[130px] w-full text-sm text-gray-900 bg-transparent border-2 ${
                      data?.status === 1 ? "border-green-500" : "border-red-500"
                    } appearance-none focus:outline-none focus:ring-0 peer`}
                    placeholder="Enter your message here..."
                  />
                  <div className="flex justify-center items-start gap-x-3">
                    <button
                      onClick={() => setData({ ...data, status: 0 })}
                      className={`shrink-0 text-red-600 bg-red-600 px-2 py-1 rounded-lg hover:opacity-80 transition-all duration-200 ease-in-out`}
                    >
                      <img src={cross} className="h-[24px]" alt="Cancel" />
                    </button>
                    <button
                      onClick={statusUpdate}
                      className={`shrink-0 text-orange-600 bg-orange-600 px-3 py-1 rounded-lg hover:opacity-80 transition-all duration-200 ease-in-out`}
                    >
                      {isLoading?.updateApplicationData ? (
                        <Loader w={"6"} h={"6"} color={"orange"} />
                      ) : (
                        <img src={send} className="h-[24px]" alt="Submit" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
